import { Link } from "@tanstack/react-router";
import { ArrowRight, Plus } from "lucide-react";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import type { DepartmentListItem } from "@/hooks/useDepartments";
import { getDepartmentIcon } from "@/lib/departmentIcons";
import { departmentUrl } from "@/lib/slugs";
import { useHomeSettings } from "@/hooks/useHomeSettings";
import { useContactSettings } from "@/hooks/useContactSettings";

export default function DepartmentsList({ departments }: { departments: DepartmentListItem[] }) {
  const { settings } = useHomeSettings();
  const { settings: contactSettings } = useContactSettings();

  return (
    <PageShell>
      <PageHero
        eyebrow="Centres of Excellence"
        title="12+ specialities under one roof"
        description="From routine consultations to complex surgeries — our multidisciplinary departments deliver coordinated, evidence-based care."
      />
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {departments.map(({ icon, name, department, description }) => {
                const Icon = getDepartmentIcon(icon);
                return (
                  <Link
                    key={name}
                    to={departmentUrl(name)}
                    className="group relative overflow-hidden rounded-2xl border border-border/70 bg-card p-7 shadow-soft transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-elevated"
                  >
                    <Plus aria-hidden className="absolute right-5 top-5 h-4 w-4 text-gold/50 transition-all duration-500 group-hover:rotate-90 group-hover:text-gold" strokeWidth={1.5} />
                    <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-primary-soft/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <div className="relative">
                      <div className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary transition-all duration-500 group-hover:scale-105">
                        <Icon className="h-7 w-7" strokeWidth={1.5} />
                        <span aria-hidden className="absolute inset-0 rounded-full ring-1 ring-gold/0 transition-all duration-500 group-hover:ring-2 group-hover:ring-gold/60 group-hover:ring-offset-2 group-hover:ring-offset-card" />
                      </div>
                      <h3 className="mt-6 font-display font-space text-xl font-semibold text-foreground transition-colors group-hover:text-primary">{department}</h3>
                      <span aria-hidden className="mt-3 block h-px w-10 bg-gold/40 transition-all duration-500 group-hover:w-20 group-hover:bg-gold" />
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gold/70 transition-all duration-500 group-hover:text-gold">
                        Explore department
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
        </div>
      </section>
    </PageShell>
  );
}
